import {
  breakdownDimensionLabels,
  type Breakdown,
  type BreakdownSegment,
} from './inventory-breakdown';

interface InventoryBreakdownProps {
  breakdown: Breakdown;
  onSelect: (segment: BreakdownSegment) => void;
}

function percent(count: number, total: number): string {
  const value = (count / total) * 100;
  return value > 0 && value < 1 ? '<1%' : `${Math.round(value)}%`;
}

/**
 * One stacked bar splitting the filtered items by the next dimension
 * (part type, then status, customer, site). Clicking a segment narrows the
 * list to it, so the bar then splits by the dimension after that.
 */
export function InventoryBreakdown({ breakdown, onSelect }: InventoryBreakdownProps) {
  const { dimension, total, segments } = breakdown;
  if (total === 0 || segments.length < 2) return null;
  const dimensionLabel = breakdownDimensionLabels[dimension];

  return (
    <section className="inventory-breakdown" aria-label={`Items by ${dimensionLabel}`}>
      <p className="inventory-breakdown-title">
        {total.toLocaleString()} {total === 1 ? 'item' : 'items'} by {dimensionLabel}
      </p>
      <div className="inventory-breakdown-bar" aria-hidden="true">
        {segments.map((segment, index) => (
          <span
            key={`${segment.key}-${segment.label}`}
            className={`inventory-breakdown-segment breakdown-color-${index}`}
            style={{ flexGrow: segment.count }}
            title={`${segment.label}: ${segment.count.toLocaleString()} (${percent(segment.count, total)})`}
          />
        ))}
      </div>
      <ul className="inventory-breakdown-legend">
        {segments.map((segment, index) => {
          const content = (
            <>
              <span className={`inventory-breakdown-swatch breakdown-color-${index}`} />
              <span className="inventory-breakdown-label">{segment.label}</span>
              <span className="inventory-breakdown-count">
                {segment.count.toLocaleString()} · {percent(segment.count, total)}
              </span>
            </>
          );
          return (
            <li key={`${segment.key}-${segment.label}`}>
              {segment.key ? (
                <button
                  type="button"
                  className="inventory-breakdown-item"
                  onClick={() => onSelect(segment)}
                  title={`Show only ${segment.label}`}
                >
                  {content}
                </button>
              ) : (
                <span className="inventory-breakdown-item">{content}</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
